import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SearchComponent } from '../../../../../_shared/components/filters/search/search.component';
import { AccountFilterComponent } from '../../../../../_shared/components';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { Subject, takeUntil } from 'rxjs';
import { AccountFilterData, WidgetFilterData } from '../../../../../_shared/models';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { CommonModule } from '@angular/common';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { RouterModule } from '@angular/router';
import { ComplianceOverviewService } from '../../../compliance-overview/services/compliance-overview.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ComplianceOverviewRequirementsRequest, ComplianceRegion, ComplianceType } from '../../../compliance-overview/models/compliance-item.model';
import { getAssetTypeLabel } from '../../../../_shared/helpers/asset-type-filter.helper';
import { AssetType } from '../../../../_shared/enums/asset-type.enum';
import { ComplianceStandard } from '../../../compliance-library/models/compliances.model';
import { startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { FilterService } from '../../../../../_shared/services';
import { ComplianceStateService } from '../../services/compliance-state.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-compliance-filters',
  standalone: true,
  imports: [
    CommonModule,
    SearchComponent,
    AccountFilterComponent,
    NgbDropdownModule,
    InfiniteScrollModule,
    RouterModule,
    TranslocoDirective
  ],
  templateUrl: './compliance-filters.component.html',
  styleUrl: './compliance-filters.component.scss'
})
export class ComplianceFiltersComponent implements OnInit, OnDestroy {
  private complianceOverviewService: ComplianceOverviewService = inject(ComplianceOverviewService);
  private complianceStateService: ComplianceStateService = inject(ComplianceStateService);
  private filterService: FilterService = inject(FilterService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private transloco: TranslocoService = inject(TranslocoService);

  @Input() hasAssetTypesBtn: boolean = false;
  @Input() hasComplianceCalendarBtn: boolean = false;
  @Input() hasComplianceRegionBtn: boolean = true;
  @Input() hasComplianceStandardBtn: boolean = true;
  @Input() isComplianceTasks: boolean = false; // either periodic or routine compliance tasks
  @Input() isComplianceLibrary: boolean = false;
  @Input() hideSearchInput: boolean = false;
  @Output() addCustomClicked = new EventEmitter<void>();
  @Output() uploadExcelClicked = new EventEmitter<void>();
  @Output() downloadTemplateClicked = new EventEmitter<void>();

  cancellation: Subject<void> = new Subject<void>();
  standardsCancellation: Subject<void> = new Subject<void>();
  accountFilterChangedSubject: Subject<number[]> = new Subject<number[]>();
  accountFilter: AccountFilterData = { accountIds: [] };
  request: ComplianceOverviewRequirementsRequest = {
    accountIds: [],
    page: 1,
    pageSize: 10
  };
  complianceRegions: ComplianceRegion[] = [];
  visibleRegions: ComplianceRegion[] = [];
  complianceTypes: ComplianceType[];
  selectedComplianceRegion?: ComplianceRegion;
  selectedComplianceType?: ComplianceType;
  complianceStandards: ComplianceStandard[] = [];
  selectedComplianceStandard: ComplianceStandard | null = null;
  isLoadingStandards: boolean = true;

  private readonly regionsPageSize = 10;
  private regionsPage = 0;
  assetType = AssetType;
  selectedAssetType: AssetType | null = null;
  isLoadingRegions: boolean = true;
  isLoadingTypes: boolean = true;

  ngOnDestroy() {
    this.cancellation.next();
    this.cancellation.complete();
    this.standardsCancellation.next();
    this.standardsCancellation.complete();
  }

  ngOnInit() {
    this.hydrateFromCurrentRequest();
    if (this.hasComplianceRegionBtn) {
      this.getComplianceRegionsAsync();
    }

    this.complianceStateService.request$
      .pipe(takeUntil(this.cancellation))
      .subscribe(request => this.syncFromRequest(request));

    if (this.hasComplianceStandardBtn) {
      this.complianceStateService.accountIds$
        .pipe(takeUntil(this.cancellation))
        .subscribe(accountIds => this.getComplianceStandardsAsync(accountIds));

      this.complianceStateService.refresh$
        .pipe(takeUntil(this.cancellation))
        .subscribe(() => this.getComplianceStandardsAsync(this.request.accountIds ?? []));
    }
  }

  /**
   * Clears the asset-type filter and emits the change to both the table/list
   * pathway and the KPI widget pathway. Parents call this when the asset-type
   * filter goes out of scope (e.g. leaving the routine-tasks tab or unmounting
   * the asset list).
   */
  public clearAssetTypeFilter(): void {
    const current = this.complianceStateService.getCurrentRequest();
    if (this.selectedAssetType === null && current?.assetTypeId == null) {
      return;
    }

    this.selectedAssetType = null;
    this.request.assetTypeId = undefined;
    this.updateComplianceRequest();
  }

  get assetTypeLabel(): string {
    if (this.selectedAssetType === null) return this.transloco.translate('compliance.shared.filters.allAssets');
    return getAssetTypeLabel(this.selectedAssetType, (key) => this.transloco.translate(key));
  }

  public onSearchTermUpdated(searchTerm: string): void {
    this.request.searchTerm = searchTerm;
    this.updateComplianceRequest();
  }

  onAccountFilterChanged(filter: AccountFilterData): void {
    if (!isEmptyObject(filter)) {
      this.accountFilter = filter;
      this.accountFilterChangedSubject.next(filter.accountIds);
      this.request.accountIds = filter.accountIds;
      this.updateComplianceRequest();
    }
  }

  async getComplianceRegionsAsync() {
    try {
      const response = await this.complianceOverviewService.getComplianceRegionsAsync(this.cancellation);
      this.complianceRegions = response.data ?? [];
      this.regionsPage = 0;
      this.visibleRegions = this.complianceRegions.slice(0, this.regionsPageSize);
      this.isLoadingRegions = false;
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoadingRegions = false;
        }
      });
    }
  }

  onRegionsScroll(): void {
    this.regionsPage++;
    const start = this.regionsPage * this.regionsPageSize;
    const next = this.complianceRegions.slice(start, start + this.regionsPageSize);
    if (next.length > 0) {
      this.visibleRegions = [...this.visibleRegions, ...next];
    }
  }

  async getComplianceStandardsAsync(accountIds: number[]): Promise<void> {
    this.standardsCancellation.next();
    this.isLoadingStandards = true;
    try {
      this.complianceStandards = await this.complianceStateService.getComplianceStandardsAsync(accountIds, this.standardsCancellation);
      this.syncSelectedComplianceStandard();
      this.isLoadingStandards = false;
      if (this.request.complianceId && !this.selectedComplianceStandard) {
        this.request.complianceId = undefined;
        this.updateComplianceRequest();
      }
    } catch (error) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoadingStandards = false;
        }
      });
    }
  }

  selectComplianceStandard(standard: ComplianceStandard | null): void {
    if (standard?.id === this.request.complianceId) {
      return;
    }
    this.selectedComplianceStandard = standard;
    this.request.complianceId = standard?.id ?? undefined;
    this.updateComplianceRequest();
  }

  clearComplianceRegion(): void {
    if (!this.selectedComplianceRegion) return;
    this.selectedComplianceRegion = undefined;
    this.request.complianceRegion = undefined;
    this.updateComplianceRequest();
  }

  selectComplianceRegion(complianceRegion: ComplianceRegion) {
    if (this.isSameComplianceRegion(complianceRegion, this.selectedComplianceRegion)) {
      return;
    }
    this.selectedComplianceRegion = complianceRegion;
    this.request.complianceRegion = complianceRegion;
    this.updateComplianceRequest();
  }

  selectComplianceType(complianceType: ComplianceType) {
    if (complianceType?.id === this.selectedComplianceType?.id) {
      return;
    }
    this.selectedComplianceType = complianceType;
    this.request.complianceType = complianceType;
    this.updateComplianceRequest();
  }

  selectAssetType(assetType: AssetType | null) {
    if (assetType === this.selectedAssetType) {
      return;
    }
    this.selectedAssetType = assetType;
    this.request.assetTypeId = assetType ?? undefined;
    this.updateComplianceRequest();
  }

  private hydrateFromCurrentRequest(): void {
    const currentRequest = this.complianceStateService.getCurrentRequest();
    if (!currentRequest) {
      return;
    }

    this.syncFromRequest(currentRequest);
    this.accountFilter = {
      accountIds: this.request.accountIds
    };
  }

  private syncFromRequest(request: ComplianceOverviewRequirementsRequest): void {
    this.request = {
      ...this.request,
      ...request,
      accountIds: request.accountIds ?? []
    };
    this.selectedComplianceRegion = request.complianceRegion;
    this.selectedComplianceType = request.complianceType;
    this.selectedAssetType = request.assetTypeId ?? null;
    this.syncSelectedComplianceStandard();
  }

  private syncSelectedComplianceStandard(): void {
    this.selectedComplianceStandard = this.complianceStandards.find(standard => standard.id === this.request.complianceId) ?? null;
  }

  private updateComplianceRequest(): void {
    this.complianceStateService.updateComplianceRequest({
      ...this.request,
      accountIds: this.request.accountIds ?? []
    });

    this.emitComplianceFilterData();
  }

  private emitComplianceFilterData(): void {
    const countryId = this.selectedComplianceRegion?.type === 'country' ? this.selectedComplianceRegion.id : undefined;
    const regionId = this.selectedComplianceRegion?.type === 'region' ? this.selectedComplianceRegion.id : undefined;
    const today = new Date();
    const startDate = startOfMonth(today);
    const endDate = endOfMonth(today);
    const previousCycleStartDate = startOfMonth(subMonths(startDate, 1));
    const previousCycleEndDate = endOfMonth(subMonths(endDate, 1));

    const filterData: WidgetFilterData = {
      accountIds: this.request.accountIds ?? [],
      regionId: regionId,
      countryId: countryId,
      complianceId: this.request.complianceId ?? undefined,
      assetTypeId: this.selectedAssetType,
      startDate,
      endDate,
      previousCycleStartDate,
      previousCycleEndDate
    } as WidgetFilterData;

    this.filterService.updateFilter(filterData);
  }

  private isSameComplianceRegion(left?: ComplianceRegion, right?: ComplianceRegion): boolean {
    return left?.id === right?.id && left?.type === right?.type;
  }
}
